import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@siliconx/shared/ui/Card';
import { Button } from '@siliconx/shared/ui/Button';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import type { BenchRunDetail } from '@siliconx/shared';
import { authorizeBenchWipe } from '../../lib/bench';
import { qk } from '../../lib/queryKeys';

const WIPE_ERROR_COPY: Record<string, string> = {
  bench_wipe_code_mismatch: 'The wipe code does not match the code on the bench screen.',
  bench_wipe_code_locked: 'Too many wrong wipe codes for this run. Pair the machine again.',
  bench_device_not_in_discovery: 'That drive was not found on this device.',
  bench_device_not_wipe_eligible: 'That drive cannot be wiped safely: it has no usable serial number or shares its identity with another drive.',
  bench_run_not_running: 'This run is no longer running.',
  bench_run_not_found: 'This run was not found.',
  bench_not_admin: 'Only an organization admin can authorize a wipe.',
};

/** The API's error `code` (carried on the thrown error's body) picks the copy; the message is a fallback key. */
function wipeErrorMessage(err: unknown): string {
  const e = err as { message?: string; body?: { error?: { code?: string } } } | null;
  const code = e?.body?.error?.code ?? e?.message ?? '';
  const key = Object.keys(WIPE_ERROR_COPY).find((k) => code.includes(k));
  return key ? WIPE_ERROR_COPY[key]! : 'The wipe authorization failed. Try again.';
}

/**
 * Wipe authorization (spec 5.2 step 5.1). Only NVMe devices from this run's
 * discovery with a usable serial and a unique identity are candidates (the API
 * marks them `wipeEligible`; one authorization names one key, so a key shared
 * by two drives would authorize both). The 6-digit wipe code is shown on the bench
 * console, so typing it proves the admin is looking at that machine. The
 * server re-checks everything (admin role, code, device in discovery, run
 * running); this panel is UX only.
 */
export function WipeAuthorizationPanel({ run }: { run: BenchRunDetail }) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [wipeCode, setWipeCode] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const nvmeDrives = run.devices.filter((d) => d.kind === 'nvme');
  const candidates = nvmeDrives.filter((d) => d.wipeEligible);
  const ineligibleCount = nvmeDrives.length - candidates.length;
  // SIL-1682: an authorization allows one wipe attempt per drive. Spent is a normal state (the
  // wipe started or finished), not a prompt to re-authorize; the Authorized pill is for unused ones.
  const spent = new Set(run.wipeSpentDeviceKeys ?? []);
  const authorized = new Set(run.wipeAuthorizedDeviceKeys.filter((k) => !spent.has(k)));
  const stagedIndexes = new Set(run.stages.filter((s) => s.destructive).map((s) => s.deviceIndex));
  const declined = new Set(run.wipeDeclinedDeviceKeys);

  // A key can become spent after a refetch while still ticked: never re-authorize it silently.
  const submitKeys = selected.filter((k) => !spent.has(k));

  const mutation = useMutation({
    mutationFn: (action: 'authorize' | 'revoke') =>
      // Declining is always safe, so it carries no wipe code (the server does not ask for one).
      authorizeBenchWipe(run.id, action === 'authorize' ? { deviceKeys: submitKeys, action, wipeCode } : { deviceKeys: submitKeys, action }),
    onSuccess: () => {
      setSelected([]);
      setWipeCode('');
      void queryClient.invalidateQueries({ queryKey: qk.bench.run(run.id) });
    },
  });

  function submit(action: 'authorize' | 'revoke') {
    return (e?: FormEvent) => {
      e?.preventDefault();
      if (submitKeys.length === 0) {
        setFormError('Select at least one drive.');
        return;
      }
      if (action === 'authorize' && !/^\d{6}$/.test(wipeCode)) {
        setFormError('Enter the 6 digits shown on the bench screen.');
        return;
      }
      setFormError(null);
      mutation.mutate(action);
    };
  }

  return (
    <Card className="space-y-3">
      <PanelTitle title="Drive wipe" />
      {nvmeDrives.length === 0 && <p className="text-sm text-muted-foreground">No NVMe drives were found on this device.</p>}
      {ineligibleCount > 0 && (
        <p className="text-sm text-muted-foreground">
          {ineligibleCount === 1 ? '1 NVMe drive' : `${ineligibleCount} NVMe drives`} cannot be wiped from here: no
          usable serial number, or the same identity as another drive.
        </p>
      )}
      {!run.canAuthorizeWipe && nvmeDrives.length > 0 && (
        <p className="text-sm text-muted-foreground">
          Only an organization admin can authorize a wipe, while the run is running.
        </p>
      )}
      <ul className="space-y-2">
        {nvmeDrives.map((d) => {
          const id = `wipe-${d.deviceIndex}`;
          const isSpent = spent.has(d.deviceKey);
          // Ticket issued but no destructive stage recorded: the wipe did not report back.
          // A stage is recorded only after the wipe finishes, so while the run is running a
          // ticket with no stage is a wipe in progress, not a lost one.
          const inProgress = isSpent && run.status === 'running' && !stagedIndexes.has(d.deviceIndex);
          const orphaned = isSpent && run.status !== 'running' && !stagedIndexes.has(d.deviceIndex);
          const selectable = run.canAuthorizeWipe && d.wipeEligible && !isSpent;
          return (
            <li key={d.deviceIndex} className="flex items-center gap-3">
              {selectable && (
                <input
                  id={id}
                  type="checkbox"
                  checked={selected.includes(d.deviceKey)}
                  onChange={(e) =>
                    setSelected((prev) =>
                      e.target.checked ? [...prev, d.deviceKey] : prev.filter((k) => k !== d.deviceKey),
                    )
                  }
                />
              )}
              {selectable ? (
                <label htmlFor={id} className="font-mono text-sm break-all">
                  {d.serial ?? 'No serial'} {d.model ? `(${d.model})` : ''}
                </label>
              ) : (
                // No checkbox in this row, so no label pointing at one.
                <span className="font-mono text-sm break-all">
                  {d.serial ?? 'No serial'} {d.model ? `(${d.model})` : ''}
                </span>
              )}
              {!d.wipeEligible && (
                <StatusPill tone="neutral" title="No usable serial number, or the same identity as another drive.">
                  Not eligible
                </StatusPill>
              )}
              {authorized.has(d.deviceKey) && <StatusPill tone="amber">Authorized</StatusPill>}
              {isSpent && (
                <StatusPill tone="neutral" title="This authorization allows one wipe attempt per drive and has been used.">
                  Authorization used
                  <span className="sr-only"> - this authorization allows one wipe attempt per drive and has been used</span>
                </StatusPill>
              )}
              {inProgress && <span className="text-xs text-muted-foreground">Wipe in progress on the bench.</span>}
              {orphaned && (
                <span className="text-xs text-muted-foreground">
                  The wipe started but no result was recorded. To wipe this drive again, pair the machine again.
                </span>
              )}
              {declined.has(d.deviceKey) && (
                <StatusPill tone="neutral" title="The bench skips this drive; it is not wiped.">Declined</StatusPill>
              )}
            </li>
          );
        })}
      </ul>
      {run.canAuthorizeWipe && candidates.length > 0 && (
        <form onSubmit={submit('authorize')} className="space-y-2">
          <label htmlFor="bench-wipe-code" className="block text-sm font-medium">
            Wipe code from the bench screen
          </label>
          <input
            id="bench-wipe-code"
            inputMode="numeric"
            autoComplete="off"
            maxLength={6}
            value={wipeCode}
            onChange={(e) => setWipeCode(e.target.value.replace(/\D/g, ''))}
            className="w-40 rounded-md border border-border bg-background px-3 py-2 font-mono text-lg tracking-widest"
          />
          {formError && <p className="text-sm text-status-red">{formError}</p>}
          {mutation.isError && <p className="text-sm text-status-red">{wipeErrorMessage(mutation.error)}</p>}
          <p className="text-xs text-muted-foreground">
            A wipe destroys all data on the selected drives and cannot be undone. Each authorization allows one wipe
            attempt per drive. Decline wipe tells the bench to skip the selected drives and needs no code.
          </p>
          <div className="flex gap-3">
            <Button type="submit" variant="destructive" disabled={mutation.isPending}>
              Authorize wipe
            </Button>
            <Button type="button" variant="secondary" disabled={mutation.isPending} onClick={() => submit('revoke')()}>
              Decline wipe
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
